const customer = {
    navigation: {
      overview: 'Overview',
      subscriptions: 'Subscriptions',
      billing: 'Billing',
      credits: 'Credits',
      teams: 'Teams',
      settings: 'Settings',
    },
    portal: {
      accountFallback: 'Customer account',
      profileComingSoon: 'Profile management coming soon',
      comingSoon: {
        intro: "We're rolling out this section of the portal in phases so it fits alongside upcoming React 19 features.",
        support: "Need to make a change today? Reach out to your provider admin or email support@florentin.app and we'll help out.",
      },
      login: {
        title: 'Access your customer portal',
        description: 'Enter the email associated with your {{provider}} subscription. We will send a 6-digit code to verify your identity.',
        passwordDescription: 'Use your anymize.ai customer account email and password to sign in.',
        emailLabel: 'Email address',
        emailPlaceholder: 'owner@example.com',
        submit: 'Send one-time code',
        submitting: 'Sending code…',
        magicTab: 'Magic link',
        passwordTab: 'Password',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Your password',
        otpLabel: 'Security code (optional)',
        passwordSubmit: 'Sign in with password',
        passwordSubmitting: 'Signing in…',
        passwordLoginDisabled: 'Password login is disabled for this customer portal.',
        invalidCredentials: 'Invalid email or password.',
        registerPrompt: 'Need to register an account?',
        registerCta: 'Create a customer profile',
        tip: 'Tip: codes expire after a few minutes. You can request a new one if it times out.',
        providerPrompt: 'Need provider access instead?',
        providerCta: 'Go to provider login',
        invalidEmail: 'Please enter a valid email address.',
        sendCodeError: 'Unable to send verification code. Please try again later.',
        usePassword: 'Use password',
        useSignInCode: 'Use sign-in code',
        forgotPassword: 'Set or reset password',
        errors: {
          emailNotVerified: 'Please verify your email address before logging in.',
        },
      },
      forgotPassword: {
        title: 'Set or reset your password',
        description: 'Enter the email associated with your customer account and we will send you a link to set or reset your password.',
        submit: 'Send password link',
        submitting: 'Sending link...',
        success: 'If an account exists for that email, a link to set or reset your password has been sent.',
        backToLogin: 'Back to login',
        error: 'Unable to send password link. Please try again.',
      },
      resetPassword: {
        title: 'Create a new password',
        description: 'Set a new password to access your customer portal.',
        passwordLabel: 'New password',
        passwordPlaceholder: 'Create a new password',
        confirmPasswordLabel: 'Confirm new password',
        confirmPasswordPlaceholder: 'Confirm your new password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        submit: 'Reset password',
        submitting: 'Resetting password...',
        success: 'Password reset successfully.',
        backToLogin: 'Back to login',
        invalidLink: 'Invalid or expired reset link.',
        invalidToken: 'Invalid or expired reset token.',
        passwordMismatch: 'Passwords do not match.',
        error: 'Unable to reset password. Please try again.',
      },
      invitation: {
        title: 'Accept Invitation',
        description: 'Set your password to complete account setup.',
        verifying: 'Verifying invitation…',
        invitationFor: 'Invitation for <strong>{{email}}</strong>',
        passwordLabel: 'Password',
        confirmPasswordLabel: 'Confirm Password',
        submitCta: 'Set Password',
        submitting: 'Setting password…',
        invalidLink: 'Invalid or expired invitation link.',
        missingParams: 'Missing invitation parameters',
        errorFallback: 'Failed to accept invitation',
      },
      verify: {
        title: 'Enter the verification code',
        description: 'We sent a 6-digit code to <strong>{{email}}</strong>. Enter it below to finish signing in.',
        error: 'Invalid or expired code. Please try again.',
        resendPrompt: "Didn't receive it?",
        resendTimer: 'Resend in {{seconds}}s',
        resendCta: 'Resend code',
        verifying: 'Verifying…',
        verifyCta: 'Verify code',
        useDifferentEmail: 'Use a different email',
        resendError: 'Unable to resend code at the moment. Please wait and try again.',
      },
  },
  customerSso: {
    missingToken: 'Missing or invalid magic link token.',
    exchangeFailed: 'Unable to complete the magic link exchange.',
    loadingTitle: 'Restoring your session…',
    loadingMessage: 'Hang tight while we sign you into the customer portal.',
    errorTitle: 'Magic link expired or invalid',
    errorMessage:
      'Request a new portal link from your provider or sign in with your email to continue.',
    backToLogin: 'Back to login',
  },
  dashboard: {
      defaultPortalName: 'Customer',
      providerFallback: 'your provider',
      welcome: 'Welcome to the {{portal}} Portal',
      subtitle: 'Track your API subscriptions, manage team access, and monitor usage activity for {{provider}} from one place.',
      cards: {
        usage: {
          title: 'Usage Insights',
          value: '{{count}} requests',
          description: 'Live API activity appears here once your team starts sending traffic.',
        },
        billing: {
          title: 'Billing',
          value: 'Payment methods',
          description: 'Review invoices, balances, and saved cards from the Billing section.',
        },
        teams: {
          title: 'Teams',
          value: 'Collaborate',
          description: 'Invite teammates, assign roles, and manage seats for shared products.',
        },
        automation: {
          title: 'Automation',
          value: 'API keys',
          description: 'Create and rotate access credentials without leaving the portal.',
        },
      },
      nextSteps: {
        title: 'Next steps',
        description: 'Use the quick checklist below to finish setting up your workspace and keep your team aligned.',
        checklist: {
          confirmBilling: 'Confirm your billing contact and payment details under Billing.',
          inviteTeammates: 'Invite teammates and assign owner/member roles from the Teams section.',
          rotateKeys: 'Rotate existing API keys and document usage policies for new members.',
        },
    },
  },
  settings: {
    title: 'Account settings',
    subtitle: 'Manage security details for your customer portal account.',
    categoriesTitle: 'Settings categories',
    categoriesDescription: 'Configure your account and platform preferences.',
    categories: {
      security: {
        title: 'Security',
        description: 'Update your password and sign-in settings.',
      },
      profile: {
        title: 'Profile',
        description: 'Manage your name and contact details.',
      },
      notifications: {
        title: 'Notifications',
        description: 'Choose how you receive updates.',
      },
      badges: {
        comingSoon: 'Coming soon',
      },
    },
    password: {
      title: 'Password',
      description: 'Update the password you use to sign in with email and password.',
      setTitle: 'Set password',
      setDescription: 'Set a password to use email and password sign-in for this account.',
      currentLabel: 'Current password',
      currentPlaceholder: 'Enter your current password',
      newLabel: 'New password',
      newPlaceholder: 'Enter a new password',
      confirmLabel: 'Confirm new password',
      confirmPlaceholder: 'Re-enter your new password',
      helper: "Your new password must meet your provider's security policy.",
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submit: 'Update password',
      submitting: 'Updating...',
      setSubmit: 'Set password',
      setSubmitting: 'Setting...',
      success: 'Password updated successfully.',
      setSuccess: 'Password set successfully.',
      error: 'Unable to update password. Please try again.',
      setError: 'Unable to set password. Please try again.',
      unauthorized: 'Please sign in again to update your password.',
      forbidden: 'Your session does not have permission to update this password.',
      validation: {
        required: 'Please fill out this field.',
        mismatch: 'Passwords do not match.',
        minLength: 'Password must be at least {{count}} characters.',
        currentIncorrect: 'The current password is incorrect.',
        useChangeEndpoint: 'Use the change password form to update your password.',
        mixed: 'Password must include upper and lower case letters.',
        symbols: 'Password must include at least one symbol.',
        numbers: 'Password must include at least one number.',
      },
    },
  },
  subscriptions: {
    title: 'Subscriptions',
    subtitle: 'Track your active plans, billing cycles, and renewal dates.',
    table: {
      plan: 'Plan',
      status: 'Status',
      billing: 'Billing',
      price: 'Price',
      period: 'Period',
      actions: 'Actions',
    },
    status: {
      active: 'Active',
      trialing: 'Trialing',
      canceled: 'Canceled',
      past_due: 'Past due',
      incomplete: 'Incomplete',
      incomplete_expired: 'Incomplete expired',
      unpaid: 'Unpaid',
      paused: 'Paused',
    },
    periodValue: '{{start}} – {{end}}',
    trialUntil: 'Trial until {{date}}',
    emptyTitle: 'No subscriptions yet',
    emptyDescription: 'When you start a subscription it will appear here with its renewal details.',
    loading: 'Loading subscriptions…',
    error: 'Unable to load subscriptions right now.',
    retry: 'Retry',
  },
  planManagement: {
    currentSubscription: {
      title: 'Your Current Subscription',
      description: 'Review your current plan details and billing status.',
      noPlan: 'No active plan',
      nextBilling: 'Next billing date',
      trialEnds: 'Trial expiry date',
      activeUntil: 'Active until',
      endedOn: 'Ended on',
      seats: 'Seat usage',
      seatUsage: '{{used}} / {{limit}} seats used',
    },
    trialExpired: {
      title: 'Your trial has expired',
      description: 'Pay now to continue using your plan.',
      payNow: 'Pay Now',
    },
    billingIssue: {
      title: 'Payment required',
      incompleteTitle: 'Payment incomplete',
      description: 'Your subscription has outstanding billing issues. Complete payment to continue.',
      cta: 'Complete payment',
    },
    status: {
      active: 'Active',
      trial: 'Trial',
      trial_expired: 'Trial expired',
      pending_cancellation: 'Pending cancellation',
      cancelled: 'Cancelled',
      past_due: 'Past due',
      incomplete: 'Incomplete',
      unpaid: 'Unpaid',
      paused: 'Paused',
      unknown: 'Unknown',
    },
    plans: {
      title: 'Plan overview',
      description: 'Compare plans and manage your subscription changes.',
      summary: '{{total}} plans available • {{actionable}} direct change options',
      current: 'Current',
      currentPlan: 'Current plan',
      customPricing: 'Contact sales',
      seatLimit: 'Up to {{count}} seats',
      moreFeatures: '+{{count}} more features',
      searchPlaceholder: 'Search plans',
      searchAria: 'Search available plans',
      emptyTitle: 'No plans found',
      emptyDescription: 'Try a different search term.',
    },
    actions: {
      upgrade: 'Upgrade',
      downgrade: 'Downgrade',
      choosePlan: 'Choose plan',
      cancelSubscription: 'Cancel subscription',
      resume: 'Resume',
      activateSubscription: 'Activate subscription',
      confirmUpgrade: 'Confirm upgrade',
      confirmDowngrade: 'Confirm downgrade',
      confirmCancel: 'Confirm cancellation',
      keepCurrent: 'Keep current plan',
      processing: 'Processing...',
      pendingChangeDisabled: 'A plan change is already scheduled. You can make another change after it takes effect.',
      unavailable: 'No direct switch',
      dismiss: 'Dismiss',
    },
    reactivation: {
      title: 'Reactivate your subscription',
      resumeAllowed: 'You can resume your subscription and continue using your plan.',
      checkoutRequired: 'Payment is required to reactivate this subscription.',
    },
    dialogs: {
      upgradeTitle: 'Upgrade to {{plan}}?',
      downgradeTitle: 'Downgrade to {{plan}}?',
      upgradeDescription: 'The upgrade takes effect immediately according to your billing settings.',
      downgradeDescription: 'The downgrade is scheduled for your next billing cycle.',
      cancelTitle: 'Cancel your subscription?',
      cancelDescription: 'Your plan stays active until the end of your current billing cycle.',
      cancelActiveUntil: 'Your plan will remain active until {{date}}.',
    },
    checkout: {
      title: 'Complete subscription checkout',
      description: 'Finish payment inside the portal to activate your subscription.',
      reactivateNoPayment: 'No payment is required to reactivate this subscription. Resume to continue.',
      noPaymentTitle: 'No payment needed',
      pendingAction: 'Pending action: {{action}}',
      backToPlans: 'Back to plans',
      autoActivationTitle: 'Activating your plan',
      autoActivationDescription: 'Your trial can be activated automatically without payment.',
      autoActivationLoading: 'Activating your trial subscription…',
      autoActivationRetry: 'Retry activation',
      summaryTitle: 'Plan summary',
      summaryDescription: 'Review the plan being activated.',
      plan: 'Plan',
      price: 'Price',
      billingCycle: 'Billing cycle',
      includedTitle: 'Included features',
      moreIncluded: '+{{count}} more included',
      paymentTitle: 'Payment details',
      paymentIntroTitle: 'Secure payment',
      paymentIntro: 'Initialize payment to load the secure card form. You will not leave the portal.',
      securityNote: 'Securely processed by Stripe.',
      cardDetails: 'Card details',
      cardDetailsHelp: 'Enter your card information below to continue.',
      billingLine1: 'Billing address',
      billingCity: 'City',
      initializePayment: 'Initialize payment',
      initializingPayment: 'Initializing payment…',
      confirmPay: 'Confirm and pay',
      processing: 'Processing payment...',
      noPaymentRequired: 'No payment is required at this time.',
      missingStripe: 'Stripe is not configured for this subscription checkout.',
      successTitle: 'Subscription activated',
      successDescription: 'Payment completed successfully. Your subscription is now active.',
      errors: {
        loadTitle: 'Unable to load checkout',
        loadDescription: 'Please try again in a moment.',
        intent: 'Unable to initialize payment.',
        clientSecretTitle: 'Unable to load payment form',
        clientSecret: 'Payment initialization returned an invalid response. Please try again.',
        requiredField: 'This field is required.',
        requiredFieldsMissing: 'Please fill out all required fields to continue.',
        billingAddressRequired: 'Billing address is required to continue.',
        unavailable: 'Checkout is not available for this subscription state.',
        confirm: 'Unable to confirm payment.',
        autoActivation: 'Unable to activate subscription automatically.',
        autoActivationPending: 'Activation is still pending. Please retry in a few seconds.',
        autoActivationRequiresPayment: 'This plan now requires payment. Please continue with checkout.',
        autoActivationMissingPlan: 'No plan was selected for activation.',
      },
    },
    errors: {
      loadTitle: 'Unable to load plan management',
      loadDescription: 'Please refresh and try again.',
    },
    feedback: {
      checkoutRequired: 'Payment is required before this change. Please complete checkout.',
      upgradeSuccess: 'Plan upgraded successfully.',
      upgradeError: 'Unable to upgrade subscription.',
      downgradeScheduled: 'Downgrade scheduled for your next billing cycle.',
      downgradeScheduledWithDate: 'Downgrade scheduled. Your current plan remains active until {{date}}.',
      downgradeError: 'Unable to schedule downgrade.',
      cancelScheduled: 'Cancellation scheduled for your next billing cycle.',
      cancelScheduledWithDate: 'Cancellation scheduled. Your plan remains active until {{date}}.',
      cancelError: 'Unable to cancel subscription.',
      resumeSuccess: 'Subscription resumed.',
      resumeError: 'Unable to resume subscription.',
    },
    paymentAction: {
      notice: 'Payment confirmation is required to complete this upgrade.',
      title: 'Confirm payment to finish upgrade',
      description: 'Your upgrade requires payment confirmation (3D Secure/SCA).',
      confirm: 'Confirm payment',
      cancel: 'Not now',
      processing: 'Confirming…',
      success: 'Payment confirmed.',
      failed: 'Payment confirmation failed.',
      contextError: 'Unable to load Stripe configuration.',
      missingStripe: 'Stripe is not configured for this portal.',
      missingClientSecret: 'Missing payment confirmation data.',
    },
    notices: {
      pendingDowngradeTitle: 'Downgrade scheduled',
      pendingDowngradeWithPlan: 'Downgrade to {{plan}} scheduled for your next billing cycle.',
      pendingDowngradeWithPlanAndDate: 'Downgrade to {{plan}} scheduled. Your current plan remains active until {{date}}.',
    },
    downgradeInfo: {
      teamTitle: 'Downgrade options are currently unavailable',
      individualTitle: 'No downgrade options are currently available',
      blockedReason: 'Reason: {{reason}}.',
      noEligible: 'No eligible downgrade options are available at the moment.',
    },
    downgradeReasons: {
      cancelled_resume_required: 'Your subscription must be resumed before you can change plans',
      cancelled_checkout_required: 'Checkout is required to reactivate this subscription',
      status_disallows_transitions: 'Plan changes are not allowed for the current subscription status',
      no_downgrade_candidates: 'No downgrade plans are available for this subscription',
      downgrade_ineligible: 'You are not eligible to downgrade at this time',
      no_eligible_downgrade: 'No eligible downgrade targets are currently available',
      seat_limit_exceeded: 'Current seat usage exceeds lower plan limits',
      quota_exceeded: 'Current quota usage exceeds lower plan limits',
      quota_check_failed: 'Quota eligibility could not be verified',
      downgrade_not_allowed: 'Downgrade is not allowed',
      same_plan: 'Already on this plan',
      pending_change_exists: 'A pending subscription change already exists',
      billing_action_required: 'Billing action is required before downgrading',
      checkout_required: 'Checkout must be completed before downgrading',
    },
  },
  billing: {
    title: 'Billing',
    subtitle: 'Manage subscriptions, invoices, and payment methods.',
    tabs: {
      subscriptions: 'Subscriptions',
      credits: 'Credits',
      invoices: 'Invoices',
      paymentMethod: 'Payment Method',
    },
    comingSoon: {
      credits: 'Track credit balances, top-ups, and usage history here soon.',
      invoices: 'Review and download invoices once billing history is available.',
      paymentMethod: 'Manage cards and billing contacts as they become available.',
    },
    paymentMethod: {
      subtitle: 'Manage the card used for subscriptions and invoices.',
      emptyTitle: 'No payment method on file',
      emptyDescription: 'Add a card to keep your subscription active and pay invoices automatically.',
      summary: {
        current: 'Current card',
        exp: 'Exp {{exp}}',
        unknown: 'Card on file',
      },
      actions: {
        add: 'Add card',
        update: 'Update card',
        remove: 'Remove',
        removing: 'Removing…',
      },
      updateDisabled: 'Payment method updates are currently disabled for this account.',
      removeBlockedTitle: 'Removal blocked',
      removeBlockedDescription: 'This payment method is required and cannot be removed right now.',
      stripeNotConfigured: 'Payments are not configured for this tenant.',
      stripeErrorTitle: 'Payment provider error',
      loadErrorTitle: 'Could not load payment method',
      loadErrorDescription: 'Please try again in a moment.',
      authErrorTitle: 'Not authorized',
      authErrorDescription: 'You’re not authorized. Please sign in again.',
      retry: 'Retry',
      removeDialog: {
        title: 'Remove payment method?',
        description: 'This will remove your default payment method from your account.',
        cancel: 'Cancel',
      },
      dialog: {
        addTitle: 'Add payment method',
        updateTitle: 'Update payment method',
        addDescription: 'Enter your card details to add a new default payment method.',
        updateDescription: 'Enter your new card details to update your default payment method.',
        loadingElement: 'Loading secure card input…',
        cancel: 'Cancel',
        confirm: 'Save',
        processing: 'Processing…',
        errors: {
          confirm: 'Unable to confirm payment method.',
          notSucceeded: 'Setup did not complete. Please try again.',
          elementLoadTitle: 'Payment input failed to load',
          elementLoad: 'Unable to load the payment input. Please check your network settings and try again.',
          stripeLoadTitle: 'Stripe failed to load',
          stripeLoad: 'Unable to load Stripe. Please check your network settings and try again.',
          intentValidateTitle: 'Unable to use setup intent',
          intentValidate: 'Unable to validate the setup intent. Please try again.',
          connectTitle: 'Stripe Connect misconfigured',
          connect: 'This tenant is using Stripe Connect but the connected account details are missing. Please contact support.',
          httpsTitle: 'Secure connection required',
          https: 'Stripe payment fields require HTTPS (or localhost). Please open this page over HTTPS and try again.',
          setupIntentTitle: 'Unable to start update',
          setupIntent: 'Unable to start payment method update.',
          contextTitle: 'Unable to load billing context',
          context: 'Please try again in a moment.',
          missingStripeTitle: 'Stripe not available',
          missingStripe: 'Stripe publishable key is missing for this tenant.',
        },
      },
      feedback: {
        updated: 'Payment method updated.',
        updateFailed: 'Unable to update payment method.',
        finishStripe: 'Finish the Stripe step, then try again.',
        refreshRequired: 'Please refresh and try again.',
        removed: 'Payment method removed.',
        removeFailed: 'Unable to remove payment method.',
        removeBlocked: 'Payment method cannot be removed right now.',
      },
    },
    invoices: {
      emptyTitle: 'No invoices yet',
      emptyDescription: 'Invoices will appear here once billing history is available.',
      loadErrorTitle: 'Could not load invoices',
      loadErrorDescription: 'Please try again in a moment.',
      authErrorTitle: 'Not authorized',
      authErrorDescription: 'You’re not authorized. Please sign in again.',
      retry: 'Retry',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
      view: 'View',
      download: 'Download PDF',
      downloadAria: 'Download invoice PDF for {{number}}',
      downloadUnavailable: 'Invoice download is not available.',
      pdfNotAvailable: 'Invoice PDF not available yet. Try again later.',
      notAuthorized: 'You’re not authorized. Please sign in again.',
      notFound: 'Invoice not found.',
      columns: {
        date: 'Date',
        number: 'Invoice',
        status: 'Status',
        amount: 'Amount',
        period: 'Billing period',
        actions: 'Actions',
      },
      status: {
        paid: 'Paid',
        open: 'Open',
        draft: 'Draft',
        void: 'Void',
        uncollectible: 'Uncollectible',
      },
    },
    credits: {
      title: 'Credits',
      subtitle: 'Monitor balances, expirations, and purchase history.',
      availableTitle: 'Available credits',
      availableDescription: 'Balance available to spend across your account.',
      expiringTitle: 'Expiring in {{days}} days',
      expiringDescription: 'Credits that will expire soon.',
      expiringCount: '{{count}} package(s)',
      expiringTableTitle: 'Expiring credits',
      expiringTableDescription: 'Credits nearing expiration based on your current balance.',
      expiringEmptyTitle: 'No credits expiring soon',
      expiringEmptyDescription: 'You have no credits expiring in the next {{days}} days.',
      historyTitle: 'Credit history',
      historyDescription: 'Purchases, usage, and remaining balances for each package.',
      historyEmptyTitle: 'No credit history yet',
      historyEmptyDescription: 'Credit purchases will appear here once available.',
      loadError: 'Unable to load credits right now.',
      retry: 'Retry',
      unknownPackage: 'Credit package',
      table: {
        package: 'Package',
        amount: 'Amount',
        purchased: 'Credits purchased',
        remaining: 'Remaining',
        status: 'Status',
        purchasedAt: 'Purchased',
        expiresAt: 'Expires',
        daysUntilExpiry: 'Days left',
      },
      status: {
        completed: 'Completed',
        pending: 'Pending',
        failed: 'Failed',
        canceled: 'Canceled',
        refunded: 'Refunded',
        expired: 'Expired',
        unknown: 'Unknown',
      },
    },
  },
  } as const;

export default customer;
